export type UserMsg = {
  content: string;
  role: "user";
};

export type BotMsg = Record<string, unknown> &
  (
    | {
        content?: string;
        role: "assistant";
        error?: boolean;
        source?: string;
        function_call?: {
          name: string;
          arguments: string;
        };
      }
    | {
        content: string;
        role: "system";
        error?: never;
        source?: never;
      }
  );

export type FuncMsg = {
  name: string;
  content: string;
  role: "function";
};

export type Msg = UserMsg | BotMsg | FuncMsg;

export type OpenAIFnParamProps = {
  [key: string]: {
    type: string;
    description: string;
    enum?: string[];
  };
};

export type OpenAIFnMeta = {
  name: string;
  description: string;
  parameters: {
    type: "object";
    properties: OpenAIFnParamProps;
    required: string[];
  };
};

export function isBotMsg(msg: Msg): msg is BotMsg {
  return msg.role !== "user";
}

export type OpenAiUsage = {
  prompt_token_quantity: number;
  completion_token_quantity: number;
};

// OpenAI Functions have two parameters - one is the metadata we send to OpenAI
// to instruct it on how to call the function, and the other is the function itself.
// We're being fairly prescriptive that the function takes its arguments as keywords
// in a Record structure, and it must return a string in order to be readable by
// OpenAI chat completions (the string will generally be a stringified version of
// a JSON response object).
export type OpenAIFunctionImpl<
  T extends Record<string, any> = Record<string, any>
> = (args: T) => string | Promise<string>;

export type OpenAIFunctions = {
  [functionName: string]: {
    meta: OpenAIFnMeta;
    func: OpenAIFunctionImpl<any>;
  };
};

export type FunctionCall = {
  name: string;
  args: string;
};
